<template>
  <div class="landing-pricing-faq">
    <KdsEyebrow>{{ t('landing.pricing.faq.eyebrow') }}</KdsEyebrow>

    <div class="landing-pricing-faq__list">
      <details
        v-for="(item, idx) in faqItems"
        :key="item.id"
        ref="detailsRefs"
        class="landing-pricing-faq__item"
        :class="{ 'landing-pricing-faq__item--last': idx === faqItems.length - 1 }"
        @toggle="onToggle(idx, $event)"
      >
        <summary class="landing-pricing-faq__summary">
          <span class="landing-pricing-faq__question">{{ item.question }}</span>
          <KdsIcon
            name="chevron-down"
            :size="18"
            class="landing-pricing-faq__chevron"
            :class="{ 'landing-pricing-faq__chevron--open': openStates[idx] }"
            aria-hidden="true"
          />
        </summary>
        <p class="landing-pricing-faq__answer">{{ item.answer }}</p>
      </details>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

const { t } = useI18n();

const detailsRefs = ref<HTMLDetailsElement[]>([]);
const openStates = ref<boolean[]>([false, false, false, false, false, false]);

const faqItems = computed(() => [
  { id: 'q1', question: t('landing.pricing.faq.q1.question'), answer: t('landing.pricing.faq.q1.answer') },
  { id: 'q2', question: t('landing.pricing.faq.q2.question'), answer: t('landing.pricing.faq.q2.answer') },
  { id: 'q3', question: t('landing.pricing.faq.q3.question'), answer: t('landing.pricing.faq.q3.answer') },
  { id: 'q4', question: t('landing.pricing.faq.q4.question'), answer: t('landing.pricing.faq.q4.answer') },
  { id: 'q5', question: t('landing.pricing.faq.q5.question'), answer: t('landing.pricing.faq.q5.answer') },
  { id: 'q6', question: t('landing.pricing.faq.q6.question'), answer: t('landing.pricing.faq.q6.answer') }
]);

const onToggle = (idx: number, event: Event) => {
  const details = event.target as HTMLDetailsElement;
  const isOpen = details.open;
  openStates.value[idx] = isOpen;
  // Sync aria-expanded on the summary for screen reader support
  const summary = details.querySelector('summary');
  if (summary) {
    summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
};

onMounted(() => {
  // Initialize aria-expanded on all summaries
  detailsRefs.value.forEach((details, idx) => {
    if (!details) return;
    const summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    }
    openStates.value[idx] = details.open;
  });
});
</script>

<style scoped lang="scss">
.landing-pricing-faq {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-width: 720px;
  margin: 0 auto;

  &__list {
    display: flex;
    flex-direction: column;
    border: var(--border);
    border-radius: var(--r-4);
    overflow: hidden;
  }

  &__item {
    border-bottom: var(--border);

    &--last {
      border-bottom: none;
    }
  }

  &__summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-5) var(--space-6);
    cursor: pointer;
    list-style: none;
    user-select: none;
    background: var(--paper);
    transition: background-color var(--d-fast) var(--ease-out);

    &::-webkit-details-marker {
      display: none;
    }

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__question {
    font-size: var(--fs-h3);
    font-weight: var(--fw-medium);
    color: var(--ink);
    line-height: 1.32;
  }

  &__chevron {
    color: var(--stone);
    flex-shrink: 0;
    transition: transform var(--d-base) var(--ease-out);

    &--open {
      transform: rotate(180deg);
    }
  }

  &__answer {
    margin: 0;
    padding: 0 var(--space-6) var(--space-5);
    font-size: var(--fs-body);
    color: var(--stone);
    line-height: 1.6;
    background: var(--paper);
  }
}

// FAQ answer animation
details[open] .landing-pricing-faq__answer {
  animation: faq-fade-in 150ms var(--ease-out);
}

@keyframes faq-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing-pricing-faq__chevron {
    transition: none !important;
  }

  details[open] .landing-pricing-faq__answer {
    animation: none !important;
  }
}
</style>
